import type { LocalizedText } from "@/math";

/**
 * In-game help. Two audiences: the player (a ten-year-old; short sentences, one idea each) and
 * the adult (teacher or parent: settings, curriculum, reports, saving). Both in PL and EN.
 */

export interface HelpSection {
  icon: string;
  title: LocalizedText;
  items: LocalizedText[];
}

export interface KeyRow {
  keys: LocalizedText;
  action: LocalizedText;
}

const s = (pl: string, en: string): LocalizedText => ({ pl, en });

export const HELP_KID: HelpSection[] = [
  {
    icon: "🧙",
    title: s("O co chodzi w grze?", "What is the game about?"),
    items: [
      s(
        "Zły czarodziej Nullus rozdarł Wielką Księgę Liczb. Z błędów w rachunkach zrobiły się potwory!",
        "The evil wizard Nullus tore up the Great Ledger of Numbers. Mistakes in sums turned into monsters!",
      ),
      s(
        "Ty jesteś bohaterem. Chodzisz po wiosce, gadasz z ludźmi, wykonujesz zadania i walczysz z potworami.",
        "You are the hero. You walk around the village, talk to people, do quests and fight monsters.",
      ),
      s(
        "Potwora zranisz tylko wtedy, gdy dobrze rozwiążesz zadanie z matematyki. Im więcej umiesz, tym jesteś silniejszy!",
        "You can only hurt a monster by solving a maths task correctly. The more you know, the stronger you are!",
      ),
    ],
  },
  {
    icon: "🚶",
    title: s("Chodzenie i rozmowy", "Walking and talking"),
    items: [
      s("Strzałki albo klawisze W, A, S, D – chodzisz.", "Arrow keys or W, A, S, D – you walk."),
      s(
        "E albo spacja – rozmawiasz z osobą, czytasz tabliczkę, otwierasz skrzynię. Stań blisko i naciśnij.",
        "E or Space – talk to a person, read a sign, open a chest. Stand close and press.",
      ),
      s(
        "W rozmowie: Enter albo spacja – następne zdanie. Gdy są przyciski do wyboru, naciśnij 1, 2 albo strzałki i Enter.",
        "In a conversation: Enter or Space – next line. When there are choices, press 1, 2 or the arrows and Enter.",
      ),
      s(
        "Na telefonie masz strzałki i przycisk na ekranie.",
        "On a phone there are arrows and a button on the screen.",
      ),
    ],
  },
  {
    icon: "⚔️",
    title: s("Walka", "Fighting"),
    items: [
      s(
        "Kiedy dotkniesz potwora, zaczyna się walka. Na ekranie pojawia się zadanie.",
        "When you touch a monster, a fight starts. A task appears on the screen.",
      ),
      s(
        "Dobra odpowiedź = twój cios ⚔️. Zła odpowiedź = potwór uderza ciebie 💔.",
        "Correct answer = your hit ⚔️. Wrong answer = the monster hits you 💔.",
      ),
      s(
        "Cztery odpowiedzi do wyboru? Naciśnij 1, 2, 3 albo 4. Trzeba wpisać wynik? Wpisz go i naciśnij Enter.",
        "Four answers to choose from? Press 1, 2, 3 or 4. Have to type the result? Type it and press Enter.",
      ),
      s(
        "Ułamek wpisujesz z kreską, np. 3/4. Przecinek albo kropka w liczbie – oba działają, np. 2,5 albo 2.5.",
        "Type a fraction with a slash, e.g. 3/4. Comma or dot in a number – both work, e.g. 2.5 or 2,5.",
      ),
      s(
        "Uważaj na zegar ⏱. Gdy czas minie, liczy się to jak zła odpowiedź.",
        "Watch the clock ⏱. When time runs out, it counts as a wrong answer.",
      ),
      s(
        "Szybka dobra odpowiedź to mocniejszy cios. Kilka dobrych z rzędu to seria 🔥 – jeszcze mocniejszy!",
        "A quick correct answer is a stronger hit. Several correct in a row is a streak 🔥 – even stronger!",
      ),
      s(
        "Pomyłka? Gra pokaże, jak rozwiązać to zadanie krok po kroku. Przeczytaj – następnym razem się uda!",
        "Made a mistake? The game shows how to solve the task step by step. Read it – next time you will get it!",
      ),
      s(
        "Wygrywasz, gdy życie potwora spadnie do zera. Dostajesz doświadczenie i monety.",
        "You win when the monster's health drops to zero. You get experience and gold.",
      ),
    ],
  },
  {
    icon: "🆘",
    title: s("Ratunek w walce", "Rescue in a fight"),
    items: [
      s(
        "H – podpowiedź. Znikną dwie złe odpowiedzi albo zobaczysz pierwszy krok rozwiązania. Zwoje z podpowiedziami kupisz u Kupca Milo.",
        "H – hint. Two wrong answers disappear or you see the first step of the solution. Buy hint scrolls from Merchant Milo.",
      ),
      s("P – wypij miksturę 🧪 i odzyskaj życie.", "P – drink a potion 🧪 and get health back."),
      s(
        "K albo F2 – kalkulator (jeśli jest włączony w ustawieniach). Enter przepisuje wynik do odpowiedzi.",
        "K or F2 – calculator (if it is enabled in the settings). Enter copies the result into the answer.",
      ),
      s(
        "Esc – ucieczka z walki. Kosztuje 3 monety, ale ratujesz życie. Od bossów nie da się uciec!",
        "Esc – run away from the fight. It costs 3 gold, but you save your health. You cannot run from bosses!",
      ),
      s(
        "Jeśli stracisz całe życie, budzisz się w wiosce i tracisz trochę monet. To wszystko – graj dalej!",
        "If you lose all your health, you wake up in the village and lose a few gold coins. That's all – keep playing!",
      ),
    ],
  },
  {
    icon: "🎓",
    title: s("Trening u Maga Quilla", "Training with Wizard Quill"),
    items: [
      s(
        "Mag Quill mieszka w domku z czerwonym dachem. U niego ćwiczysz bez zegara i bez utraty życia.",
        "Wizard Quill lives in the house with the red roof. With him you practise without a clock and without losing health.",
      ),
      s(
        "Wybierasz temat i poziom trudności (1 – łatwy, 3 – trudny). Podpowiedzi są za darmo.",
        "You choose a topic and a difficulty (1 – easy, 3 – hard). Hints are free.",
      ),
      s(
        "Za każdą dobrą odpowiedź dostajesz 1 punkt doświadczenia. To najlepsze miejsce, żeby nauczyć się nowego tematu przed walką.",
        "Every correct answer gives you 1 experience point. It is the best place to learn a new topic before a fight.",
      ),
    ],
  },
  {
    icon: "📜",
    title: s("Zadania, sklep i skarby", "Quests, shop and treasure"),
    items: [
      s(
        "Starsza Abacia i Kupiec Milo dają zadania, np. pokonaj 5 Blobbli. Nagroda: monety, doświadczenie i przedmioty, np. Różdżka Skupienia dodaje 5 sekund do każdego pytania.",
        "Elder Abacia and Merchant Milo give quests, e.g. defeat 5 Blobbles. Rewards: gold, experience and items, e.g. the Wand of Focus adds 5 seconds to every question.",
      ),
      s(
        "Twoje zadania widzisz w rogu ekranu i w plecaku. Gdy zadanie jest gotowe ✅, wróć do osoby, która je dała.",
        "Your quests are shown in the corner of the screen and in the backpack. When a quest is ready ✅, go back to the person who gave it.",
      ),
      s(
        "U Milo kupisz mikstury, zwoje z podpowiedziami, miecze i zbroje. Po naprawie mostu ma więcej towarów.",
        "Milo sells potions, hint scrolls, swords and armour. After the bridge is repaired he has more goods.",
      ),
      s(
        "Świat ma pięć krain: wioskę Countinghollow, Zielone Łąki, Szepczący Las, Ruiny Starej Rachuby z kopalnią i Wybrzeże Konserw. W każdej ktoś ma dla ciebie zadanie – czytaj drogowskazy.",
        "The world has five lands: the village of Countinghollow, the Green Meadows, the Whispering Forest, the Ruins of Old Reckoning with the mine, and the Cannery Coast. In each one somebody has a quest for you – read the signposts.",
      ),
      s(
        "Skrzyń 🎁 na mapie pilnują potwory. Możesz walczyć o skarb albo odejść – nic ci się nie stanie.",
        "Chests 🎁 on the map are guarded by monsters. You can fight for the treasure or walk away – nothing happens to you.",
      ),
    ],
  },
  {
    icon: "🎒",
    title: s("Plecak, poziomy i postępy", "Backpack, levels and progress"),
    items: [
      s(
        "I albo Tab – otwierasz plecak. Zakładasz tam miecze i zbroje, używasz mikstur, sprawdzasz zadania.",
        "I or Tab – open the backpack. Put on swords and armour, use potions, check your quests.",
      ),
      s(
        "Za dobre odpowiedzi rośnie doświadczenie (PD). Pełny pasek = nowy poziom: więcej życia i mocniejszy cios.",
        "Correct answers grow your experience (XP). A full bar = a new level: more health and a stronger hit.",
      ),
      s(
        "W zakładce Postępy widzisz, które tematy idą ci dobrze, a które warto poćwiczyć u Maga Quilla.",
        "The Progress tab shows which topics go well and which are worth practising with Wizard Quill.",
      ),
    ],
  },
  {
    icon: "💾",
    title: s("Zapisywanie i bohaterowie", "Saving and heroes"),
    items: [
      s(
        "Gra zapisuje się sama. 🏠 Menu (albo M w plecaku) zapisuje i wraca do listy bohaterów.",
        "The game saves by itself. 🏠 Menu (or M in the backpack) saves and returns to the hero list.",
      ),
      s(
        "Każdy w rodzinie może mieć własnego bohatera. Na ekranie startowym wybierz swojego i naciśnij Graj.",
        "Everyone in the family can have their own hero. On the start screen pick yours and press Play.",
      ),
      s(
        "Gdy dorosły zaloguje się do chmury ☁, twój bohater będzie też na innym komputerze albo telefonie.",
        "When an adult signs in to the cloud ☁, your hero is also on another computer or phone.",
      ),
    ],
  },
];

export const HELP_ADULT: HelpSection[] = [
  {
    icon: "📘",
    title: s("Czym jest MathRPG?", "What is MathRPG?"),
    items: [
      s(
        "Gra przygodowa, w której każdy cios w walce to zadanie z matematyki z wybranej klasy (szkoła podstawowa, klasy 4–8). Bez poprawnych odpowiedzi nie da się pokonać potworów ani zdobywać poziomów.",
        "An adventure game where every hit in a fight is a maths task from the chosen school level (Polish primary school klasa 4–8, roughly ages 10–14). Without correct answers there is no way to defeat monsters or gain levels.",
      ),
      s(
        "Zadania są generowane losowo (setki wariantów w każdym temacie), w trzech poziomach trudności, po polsku i po angielsku, z rozwiązaniem krok po kroku po każdej pomyłce.",
        "Tasks are generated randomly (hundreds of variants per topic) at three difficulty tiers, in Polish and English, with a step-by-step solution after every mistake.",
      ),
      s(
        "Sesja 15–20 minut daje zwykle 20–40 rozwiązanych zadań.",
        "A 15–20 minute session usually yields 20–40 solved tasks.",
      ),
    ],
  },
  {
    icon: "📚",
    title: s("Zakres materiału", "Curriculum"),
    items: [
      s(
        "Każda klasa ma 10–11 tematów zgodnych z podstawą programową, np. klasa 6: liczby całkowite, kolejność działań, ułamki i procenty, proporcje, równania, prędkość–droga–czas, kąty w trójkącie, pola figur, średnia.",
        "Each level has 10–11 topics following the Polish core curriculum, e.g. klasa 6: integers, order of operations, fractions and percent, ratios, simple equations, speed–distance–time, triangle angles, composite areas, averages.",
      ),
      s(
        "Przycisk „Zakres materiału dla nauczyciela” (ekran startowy lub plecak → Postępy) drukuje listę tematów danej klasy z przykładowymi zadaniami i odpowiedziami.",
        "The “Curriculum overview” button (start screen, or backpack → Progress) prints the topic list of a level with sample tasks and answers.",
      ),
      s(
        "Klasę można wybrać niezależnie od wieku dziecka – niższą dla utrwalenia, wyższą jako wyzwanie.",
        "The level can be chosen independently of the child's age – lower to consolidate, higher as a challenge.",
      ),
    ],
  },
  {
    icon: "⚙️",
    title: s("Ustawienia przy tworzeniu bohatera", "Settings when creating a hero"),
    items: [
      s(
        "Odpowiedzi: „przyciski” – 4 odpowiedzi do wyboru (mniej pisania, łatwiej zgadnąć); „wpisywanie” – dziecko wpisuje wynik (wymaga prawdziwego policzenia); „domyślnie dla klasy” – przyciski w klasie 4, wpisywanie od klasy 5.",
        "Answers: “buttons” – 4 options (less typing, easier to guess); “typing” – the child types the result (requires real calculation); “level default” – buttons in klasa 4, typing from klasa 5.",
      ),
      s(
        "Czas na odpowiedź: domyślnie bez limitu; można włączyć 20–60 s, gdy dziecko chce się ścigać z zegarem (szybkie odpowiedzi biją mocniej).",
        "Time per question: no limit by default; switch on 20–60 s when the child wants to race the clock (quick answers hit harder).",
      ),
      s(
        "Kalkulator: włączony pozwala skupić się na metodzie (np. procenty, równania), wyłączony ćwiczy rachunek pamięciowy i pisemny.",
        "Calculator: enabled lets the child focus on the method (percent, equations), disabled trains mental and written arithmetic.",
      ),
      s(
        "Tematy: przy tworzeniu bohatera można odznaczyć tematy, których jeszcze nie było w szkole, albo zostawić tylko te do powtórki. Potwory i trening „mieszany” pytają wtedy tylko z zaznaczonych; w treningu nadal można wybrać każdy temat. Zmiana później: przycisk ⚙ przy bohaterze na ekranie startowym.",
        "Topics: when creating a hero you can untick topics the school has not covered yet, or keep only the ones to revise. Monsters and “mixed” training then ask only from the ticked ones; in training any topic can still be chosen. Change it later with the ⚙ button next to the hero on the start screen.",
      ),
      s(
        "Ustawienia są zapisane razem z bohaterem, więc każde dziecko może mieć inne.",
        "Settings are saved with the hero, so each child can have different ones.",
      ),
    ],
  },
  {
    icon: "🧠",
    title: s("Jak gra uczy", "How the game teaches"),
    items: [
      s(
        "Dobór zadań jest adaptacyjny: tematy, w których dziecko myli się częściej, pojawiają się około trzy razy częściej, a trudność rośnie wraz z poziomem bohatera.",
        "Task selection is adaptive: topics where the child makes more mistakes appear about three times as often, and difficulty rises with the hero's level.",
      ),
      s(
        "Po każdej pomyłce gra pokazuje pełne rozwiązanie. Warto zachęcić dziecko, żeby je przeczytało, zanim naciśnie „dalej”.",
        "After every mistake the game shows the full solution. Encourage the child to read it before pressing “continue”.",
      ),
      s(
        "Trening u Maga Quilla to tryb bez zegara i bez utraty życia, z darmowymi podpowiedziami – dobre miejsce na nowy temat albo na nadrabianie zaległości.",
        "Training with Wizard Quill is a mode without a clock or health loss, with free hints – a good place for a new topic or for catching up.",
      ),
      s(
        "Kara za błędy jest łagodna (utrata życia, przy przegranej kilka monet), a nagrody za serie poprawnych odpowiedzi są wyraźne – to zachęca do liczenia, nie do zgadywania.",
        "The penalty for mistakes is mild (health, a few gold coins on defeat) while rewards for streaks of correct answers are clear – this encourages calculating rather than guessing.",
      ),
    ],
  },
  {
    icon: "📊",
    title: s("Postępy i raport dla nauczyciela", "Progress and the teacher report"),
    items: [
      s(
        "Plecak → Postępy pokazuje skuteczność w każdym temacie (poprawne / wszystkie) i zaznacza tematy słabsze.",
        "Backpack → Progress shows accuracy per topic (correct / total) and marks weak topics.",
      ),
      s(
        "„Raport dla nauczyciela” wypisuje każde zadanie, które bohater rozwiązał: treść, poprawną odpowiedź, odpowiedź dziecka, czas, tryb (walka lub trening) oraz podsumowanie po tematach. Raport otwiera się w widoku do druku – wybierz „Zapisz jako PDF”.",
        "“Teacher report” lists every task the hero answered: the question, the correct answer, the child's answer, time, mode (battle or training) and a summary per topic. It opens in a print view – choose “Save as PDF”.",
      ),
      s(
        "Raportu nie da się edytować w grze. Na dole jest kod weryfikacyjny wyliczony z treści raportu: ten sam bohater i te same zadania dają zawsze ten sam kod, więc dwa wydruki można porównać.",
        "The report cannot be edited in the game. At the bottom is a verification code computed from the report's content: the same hero and the same tasks always give the same code, so two printouts can be compared.",
      ),
      s("Historia obejmuje ostatnie 3000 zadań bohatera.", "The history covers the hero's last 3000 tasks."),
    ],
  },
  {
    icon: "👨‍👩‍👧",
    title: s("Kilku graczy, zapis i chmura", "Several players, saving and the cloud"),
    items: [
      s(
        "Każdy gracz tworzy własnego bohatera na ekranie startowym; postępy, ustawienia i historia są osobne. Bohatera można usunąć ikoną 🗑.",
        "Each player creates their own hero on the start screen; progress, settings and history are separate. A hero can be deleted with the 🗑 icon.",
      ),
      s(
        "Gra zapisuje się sama (po walce, rozmowie, zakupie i co chwilę podczas chodzenia) w przeglądarce na tym urządzeniu.",
        "The game saves itself (after fights, conversations, purchases and regularly while walking) in the browser on this device.",
      ),
      s(
        "Po zalogowaniu kontem Microsoft lub GitHub (☁ na ekranie startowym) bohaterowie są też kopiowani do chmury i pojawiają się na każdym urządzeniu, na którym zalogujesz się tym samym kontem. Zaloguj się jako dorosły – dziecko nie potrzebuje własnego konta.",
        "After signing in with a Microsoft or GitHub account (☁ on the start screen) heroes are also copied to the cloud and appear on every device where you sign in with the same account. Sign in as the adult – the child does not need an account.",
      ),
      s(
        "Gra przechowuje tylko imię bohatera, klasę, postępy i historię zadań. Nie zna haseł ani adresu e-mail dziecka.",
        "The game stores only the hero's name, level, progress and task history. It knows no passwords and no e-mail address of the child.",
      ),
    ],
  },
  {
    icon: "💡",
    title: s("Wskazówki", "Tips"),
    items: [
      s(
        "Nowy temat najpierw u Maga Quilla (poziom 1), dopiero potem walki.",
        "New topic first with Wizard Quill (tier 1), fights afterwards.",
      ),
      s(
        "Krótkie, regularne sesje (15–20 minut) działają lepiej niż długie.",
        "Short, regular sessions (15–20 minutes) work better than long ones.",
      ),
      s(
        "Raz w tygodniu przejrzyjcie razem raport: które tematy są słabsze, jakie błędy się powtarzają.",
        "Once a week look through the report together: which topics are weaker, which mistakes repeat.",
      ),
      s(
        "Gdy dziecko się frustruje: wyłącz zegar, włącz przyciski albo obniż klasę. Gdy się nudzi: wpisywanie, krótszy czas, wyższa klasa.",
        "When the child gets frustrated: turn off the timer, switch to buttons or lower the level. When bored: typing, shorter time, higher level.",
      ),
      s(
        "Przełącznik języka (PL/EN) zmienia też treść zadań – można ćwiczyć matematykę po angielsku.",
        "The language switch (PL/EN) also changes the tasks – maths can be practised in English.",
      ),
      s(
        "Wszystko działa z klawiatury, bez myszy; na telefonie i tablecie są przyciski ekranowe.",
        "Everything works from the keyboard, without a mouse; phones and tablets get on-screen buttons.",
      ),
    ],
  },
];

export const HELP_KEYS: KeyRow[] = [
  { keys: s("← ↑ ↓ →  albo  W A S D", "← ↑ ↓ →  or  W A S D"), action: s("Ruch", "Move") },
  {
    keys: s("E  albo  spacja", "E  or  Space"),
    action: s("Rozmowa, tabliczka, skrzynia", "Talk, sign, chest"),
  },
  { keys: s("1 2 3 4", "1 2 3 4"), action: s("Wybór odpowiedzi", "Choose an answer") },
  {
    keys: s("Enter", "Enter"),
    action: s("Zatwierdź odpowiedź, następne zdanie", "Confirm the answer, next line"),
  },
  { keys: s("H", "H"), action: s("Podpowiedź", "Hint") },
  { keys: s("P", "P"), action: s("Mikstura", "Potion") },
  { keys: s("K  albo  F2", "K  or  F2"), action: s("Kalkulator", "Calculator") },
  { keys: s("Esc", "Esc"), action: s("Ucieczka z walki, zamknięcie okna", "Run away, close a window") },
  { keys: s("I  albo  Tab", "I  or  Tab"), action: s("Plecak", "Backpack") },
  { keys: s("Z", "Z"), action: s("Zadania", "Quests") },
  { keys: s("P (w wiosce)", "P (in the world)"), action: s("Postępy", "Progress") },
  {
    keys: s("M (w plecaku)", "M (in the backpack)"),
    action: s("Zapisz i wróć do menu", "Save and go to the menu"),
  },
  { keys: s("F1  albo  ?", "F1  or  ?"), action: s("Ta pomoc", "This help") },
];
